import { QueryClient } from '@tanstack/react-query';
import {
  BookmarkCollectionResponse,
  BookmarkResponse,
  BookmarkContentResponse,
  BookmarkAction,
  BookmarkActionsRequest,
  BookmarkSectionActionRequestBody,
  BookmarkSectionActionResponse,
  BookmarkSectionActionSection,
  BookmarkSectionActionTemplate,
  ProfileData,
  HighlightsResponse,
  User,
  UserSessionResponse,
} from '../types/api';
import { redirectToLogin } from '../utils/authRedirect';

const providerQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export class APIError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'APIError';
    this.status = status;
  }
}

// Every mutating /data/* endpoint requires two CSRF credentials (backend:
// _checks_data_csrf in frontend/lib/__init__.py): the X-Requested-With header,
// which a cross-site <form> cannot set, and the user's form_key.
//
// The token is read from the currentUser query rather than kept in a module
// variable, so there is one source of truth: useCurrentUser already stores it
// (transformed to camelCase formKey) under this key, and the value stays correct
// when the query is invalidated -- form_key is derived from the password hash,
// so it rotates on a password change. providerQueryClient is the client the app
// mounts in main.tsx, which is what makes this lookup valid outside React.
function getFormKey(): string | null {
  const user = providerQueryClient.getQueryData<User | null>(['currentUser']);
  return user?.formKey || null;
}

/**
 * CSRF headers for a /data/* request. Callers already holding a form_key (the
 * services that take one as a param) pass it explicitly; everyone else falls
 * back to the session's current token.
 */
function dataHeaders(
  extra: Record<string, string> = {},
  formKeyOverride?: string,
): Record<string, string> {
  const formKey = formKeyOverride || getFormKey();
  return {
    'X-Requested-With': 'XMLHttpRequest',
    ...(formKey ? { 'X-Form-Key': formKey } : {}),
    ...extra,
  };
}

// Parses the cumulative body streamed by POST /data/summarize. The endpoint
// emits a single `key_sentences: [...]\n\n` record followed by repeated
// `delta: <text>\n\n` records. The key_sentences payload is JSON (no literal
// newlines), so the first `\n\n` reliably terminates it; everything after is the
// streamed overview. Parsing the full buffer on each chunk keeps it resilient to
// chunk boundaries splitting a record. Mirrors processSummaryData in read.coffee.
function parseSummaryStream(raw: string): { keySentences: string[]; overview: string } {
  const ksPrefix = 'key_sentences: ';
  const deltaPrefix = 'delta: ';

  let keySentences: string[] = [];
  let deltaPortion = raw;

  const ksIdx = raw.indexOf(ksPrefix);
  if (ksIdx !== -1) {
    const afterKs = raw.slice(ksIdx + ksPrefix.length);
    const sepIdx = afterKs.indexOf('\n\n');
    const jsonPart = sepIdx === -1 ? afterKs : afterKs.slice(0, sepIdx);
    try {
      const parsed = JSON.parse(jsonPart.trim());
      if (Array.isArray(parsed)) {
        keySentences = parsed;
      }
    } catch {
      // The key_sentences JSON has not finished streaming yet.
    }
    deltaPortion = sepIdx === -1 ? '' : afterKs.slice(sepIdx + 2);
  }

  let overview = '';
  if (deltaPortion.includes(deltaPrefix)) {
    for (const part of deltaPortion.split(deltaPrefix)) {
      overview += part.replace(/(\n\n)+$/, '');
    }
  }

  return { keySentences, overview };
}

export interface SummarizeCallbacks {
  onKeySentences?: (sentences: string[]) => void;
  onDelta?: (overview: string) => void;
}

export interface SummarizeParams {
  bookmarkId: number | string;
  formKey: string;
  keySentences?: string[];
  text?: string;
}

function previewResponseBody(body: string, maxLength = 120): string {
  const trimmed = body.trim();
  if (!trimmed) {
    return '(empty body)';
  }
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength)}...`;
}

async function parseJsonResponse<T>(
  response: Response,
  url: string,
  body: string,
): Promise<T> {
  try {
    return JSON.parse(body) as T;
  } catch {
    throw new APIError(
      `Invalid JSON from ${url}: ${previewResponseBody(body)}`,
      response.status,
    );
  }
}

interface FetchFromDataOptions {
  /** Redirect to the login page on a 401. Defaults to true — turn it off for
   *  endpoints where an anonymous 401 is expected (e.g. the session probe on a
   *  public page), so logged-out visitors aren't bounced to /user/login. */
  redirectOnUnauthorized?: boolean;
}

// Base "Internal API" (ie: ^/data/*) request function for data endpoints
async function fetchFromData<T>(
  endpoint: string,
  options: RequestInit = {},
  { redirectOnUnauthorized = true }: FetchFromDataOptions = {},
): Promise<T> {
  const url = endpoint.startsWith('/') ? `/data${endpoint}` : `/data/${endpoint}`;

  // options is spread first so an explicit `headers` in it cannot clobber the
  // merged header object (which would drop the CSRF credentials below).
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...dataHeaders(),
      ...options.headers,
    },
  });

  const body = await response.text();

  if (!response.ok) {
    // A 401 from a /data/* endpoint normally means the session is gone (expired,
    // or logged out in another tab). Send the user to the login page instead of
    // letting the error bubble up to a generic "Something went wrong" screen —
    // unless the caller expects anonymous 401s (see redirectOnUnauthorized).
    if (response.status === 401 && redirectOnUnauthorized) {
      redirectToLogin();
    }
    const errorData = await parseJsonResponse<Record<string, string>>(
      response,
      url,
      body,
    ).catch(() => null);
    throw new APIError(
      errorData?.message || `API error: ${response.status} ${response.statusText}`,
      response.status,
    );
  }

  return parseJsonResponse<T>(response, url, body);
}

// based on handlers available in DataHandlers.py
const client = {
  userSession: () => {
    // The session probe is an identity check, not an auth gate: a 401 just means
    // "not logged in", which is a valid state on public pages (landing, profiles,
    // docs). Never redirect here — resolve to a "no user" state and let the caller
    // decide. Auth-gated routes redirect via the route gate (useAuthRedirect under
    // /_app) when the user turns out to be absent.
    return fetchFromData<UserSessionResponse>(
      '/user_session',
      {},
      { redirectOnUnauthorized: false },
    );
  },

  bookmark: (bookmarkId: number | string) => {
    return fetchFromData<BookmarkResponse>(`/bookmarks/${bookmarkId}`);
  },

  bookmarkActions: (actions: BookmarkAction[]) => {
    const request: BookmarkActionsRequest = { actions };
    return fetchFromData(`/bookmarks/actions`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  bookmarkSectionAction: (
    section: BookmarkSectionActionSection,
    action: BookmarkSectionActionTemplate,
  ) => {
    const body: BookmarkSectionActionRequestBody = { section, action };
    return fetchFromData<BookmarkSectionActionResponse>(`/sections/actions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  bookmarkContent: (bookmarkId: number | string) => {
    return fetchFromData<BookmarkContentResponse>(`/bookmarks/${bookmarkId}/content`);
  },

  bookmarks: (params: {
    section_type: 'home' | 'liked' | 'archive' | 'folder' | 'tag' | 'videos';
    page?: number;
    folder_id?: string;
    tag?: string;
    include_user?: boolean;
    sort?: 'newest' | 'oldest';
  }) => {
    const searchParams = new URLSearchParams();

    // Add required section_type
    searchParams.append('section_type', params.section_type);

    // Add optional parameters
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.folder_id) searchParams.append('folder_id', params.folder_id);
    if (params.tag) searchParams.append('tag', params.tag);
    if (params.include_user) searchParams.append('include_user', 'true');
    if (params.sort) searchParams.append('sort', params.sort);

    return fetchFromData<BookmarkCollectionResponse>(
      `/bookmarks?${searchParams.toString()}`,
    );
  },

  createHighlight: (
    bookmarkId: number | string,
    params: {
      text: string;
      position?: number;
      note?: string;
    },
  ) => {
    const formData = new URLSearchParams();
    formData.append('text', params.text);
    if (params.position !== undefined)
      formData.append('position', params.position.toString());
    if (params.note) formData.append('note', params.note);

    return fetch(`/data/bookmark/${bookmarkId}/highlight`, {
      method: 'POST',
      headers: dataHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
      credentials: 'same-origin',
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },

  deleteHighlight: (highlightId: number | string, formKey?: string) => {
    const formData = new URLSearchParams();
    if (formKey) {
      formData.append('form_key', formKey);
    }

    return fetch(`/data/highlight/${highlightId}/delete`, {
      method: 'POST',
      headers: dataHeaders(
        { 'Content-Type': 'application/x-www-form-urlencoded' },
        formKey,
      ),
      credentials: 'same-origin',
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },

  keySentences: (bookmarkId: number | string, text: string) => {
    const formData = new URLSearchParams();
    formData.append('text', text);

    return fetch(`/data/bookmarks/${bookmarkId}/key_sentences`, {
      method: 'POST',
      headers: dataHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
      credentials: 'same-origin',
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },

  progress: (bookmarkId: number | string, progress: number) => {
    const params = new URLSearchParams();
    params.append('progress', `${progress}`);
    params.append('ajax', '1');

    // POST, not GET: this is a write. The endpoint still accepts GET for older
    // client bundles, but a mutating GET is reachable from a cross-site
    // <img>/<link> in a way no header check can cover.
    return fetch(`/data/bookmarks/${bookmarkId}/progress`, {
      method: 'POST',
      headers: dataHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      credentials: 'same-origin',
      body: params.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    });
  },

  updateHighlightNote: (highlightId: number | string, note: string, formKey: string) => {
    const formData = new URLSearchParams();
    formData.append('note', note);
    formData.append('form_key', formKey);

    return fetch(`/data/highlight/${highlightId}/update_note`, {
      method: 'POST',
      headers: dataHeaders(
        { 'Content-Type': 'application/x-www-form-urlencoded' },
        formKey,
      ),
      credentials: 'same-origin',
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      // The server returns an empty response for successful updates
      // Check if there's any content to parse
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      } else {
        // Return a success object for empty responses
        return { success: true };
      }
    });
  },

  addBookmark: async (url: string) => {
    const response = await fetch('/data/bookmarks/create', {
      method: 'POST',
      headers: dataHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'same-origin',
      body: JSON.stringify({ url }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      }
      throw new Error(data.error || 'Failed to add bookmark');
    }

    return data.bookmark;
  },

  profile: async (userId: string, page = 1, folderId?: string) => {
    const search = new URLSearchParams({ page: `${page}` });
    if (folderId) {
      search.set('folder_id', folderId);
    }
    const response = await fetch(`/data/profile/${userId}?${search}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new APIError('User not found or profile is private', 404);
      }
      throw new APIError(`Failed to fetch profile: ${response.status}`, response.status);
    }
    const data = (await response.json()) as ProfileData;
    return data;
  },

  updateBookmark: (
    bookmarkId: number | string,
    params: {
      title: string;
      url: string;
      description?: string;
    },
  ) => {
    return fetchFromData(`/bookmarks/${bookmarkId}/edit`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  highlights: (
    params: {
      page?: number;
      include_user?: boolean;
    } = {},
  ) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.include_user) searchParams.append('include_user', 'true');

    return fetchFromData<HighlightsResponse>(`/notes?${searchParams.toString()}`);
  },

  // Generates an article summary via the streaming POST /data/summarize endpoint.
  // Sends pre-computed key_sentences when available (Premium "summary highlights"
  // path) to avoid recomputation, otherwise sends the article text. Invokes the
  // callbacks progressively as the overview streams in, and resolves with the
  // final overview + key sentences. Throws an APIError carrying the HTTP status
  // (e.g. 412 = Premium required, 429 = rate limited, 422 = text too short).
  summarize: async (
    params: SummarizeParams,
    callbacks: SummarizeCallbacks = {},
    signal?: AbortSignal,
  ): Promise<{ overview: string; keySentences: string[] }> => {
    const body = new URLSearchParams();
    body.append('form_key', params.formKey);
    body.append('bookmark_id', String(params.bookmarkId));
    if (params.keySentences && params.keySentences.length > 0) {
      body.append('key_sentences', JSON.stringify(params.keySentences));
    } else if (params.text) {
      body.append('text', params.text);
    }

    const response = await fetch('/data/summarize', {
      method: 'POST',
      headers: dataHeaders(
        { 'Content-Type': 'application/x-www-form-urlencoded' },
        params.formKey,
      ),
      credentials: 'same-origin',
      body: body.toString(),
      signal,
    });

    if (!response.ok) {
      throw new APIError(`Summarize failed: ${response.status}`, response.status);
    }

    let raw = '';
    let lastKeySentencesLen = 0;

    const emit = () => {
      const parsed = parseSummaryStream(raw);
      if (parsed.keySentences.length !== lastKeySentencesLen) {
        lastKeySentencesLen = parsed.keySentences.length;
        callbacks.onKeySentences?.(parsed.keySentences);
      }
      callbacks.onDelta?.(parsed.overview);
      return parsed;
    };

    const reader = response.body?.getReader();
    if (reader) {
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        raw += decoder.decode(value, { stream: true });
        emit();
      }
      raw += decoder.decode();
    } else {
      // Environments without a streaming body (e.g. some test mocks): read once.
      raw = await response.text();
    }

    return emit();
  },

  seenCookieNotice: (formKey: string) => {
    return fetchFromData<{ success: boolean }>(
      `/user/seen_cookie_notice?form_key=${encodeURIComponent(formKey)}`,
      {
        method: 'POST',
        body: '',
      },
    );
  },
};

export { client, dataHeaders, fetchFromData, providerQueryClient };
